/**
 * Option sources for the visits list filter toolbar.
 *
 * - Brands and market-developers are small, slow-changing reference lists →
 *   loaded once and cached via React Query (5-min stale time).
 * - Stores are numerous (600+), so they are searched server-side: the toolbar's
 *   store dropdown drives `searchStores(term)` (debounced inside the dropdown),
 *   which we feed to a term-keyed query hitting `GET /stores?search=`.
 *
 * All three reuse existing, already-wired sources from sibling features rather
 * than introducing new API code.
 */

import {useState} from 'react';
import {useQuery} from '@tanstack/react-query';
import type {SearchableDropdownOption} from '../../../Presentation/components/ui/SearchableDropdown';
import {productFactory} from '../../products/store/productFactory';
import {MarketerAPIDataSource} from '../../marketers/api/MarketerAPIDataSource';
import {StoreAPIDataSource} from '../../stores/api/StoreAPIDataSource';

/** Reference lists change slowly; cache for 5 minutes before considering stale. */
const STALE_TIME = 5 * 60 * 1000;
/** How many stores to return per server-side search. */
const STORE_SEARCH_LIMIT = 20;

export const VISIT_FILTER_OPTION_KEYS = {
  brands: ['visit-filter-options', 'brands'] as const,
  developers: ['visit-filter-options', 'developers'] as const,
  stores: (search: string) => ['visit-filter-options', 'stores', search] as const
};

export interface UseVisitFilterOptionsResult {
  brandOptions: SearchableDropdownOption[];
  brandsLoading: boolean;
  developerOptions: SearchableDropdownOption[];
  developersLoading: boolean;
  storeOptions: SearchableDropdownOption[];
  storesLoading: boolean;
  /** Feed the store dropdown's `onSearch` here to drive server-side search. */
  searchStores: (term: string) => void;
}

export const useVisitFilterOptions = (): UseVisitFilterOptionsResult => {
  const [storeSearch, setStoreSearch] = useState('');

  const brandsQuery = useQuery({
    queryKey: VISIT_FILTER_OPTION_KEYS.brands,
    queryFn: () => productFactory.getGetAllBrandsUseCase().execute(),
    staleTime: STALE_TIME
  });

  const developersQuery = useQuery({
    queryKey: VISIT_FILTER_OPTION_KEYS.developers,
    queryFn: () => MarketerAPIDataSource.getAllWithoutPagination(),
    staleTime: STALE_TIME
  });

  const trimmedStoreSearch = storeSearch.trim();
  const storesQuery = useQuery({
    queryKey: VISIT_FILTER_OPTION_KEYS.stores(trimmedStoreSearch),
    queryFn: () => StoreAPIDataSource.getAll(1, STORE_SEARCH_LIMIT, {search: trimmedStoreSearch}),
    // Only hit the network once the user has typed something searchable.
    enabled: trimmedStoreSearch.length >= 2,
    staleTime: STALE_TIME
  });

  const brandOptions: SearchableDropdownOption[] = (brandsQuery.data ?? []).map(brand => ({
    value: String(brand.id),
    label: brand.name
  }));

  const developerOptions: SearchableDropdownOption[] = (developersQuery.data?.items ?? []).map(dev => ({
    value: String(dev.id),
    label: `${dev.fullName} (${dev.username})`,
    searchText: `${dev.fullName} ${dev.username}`
  }));

  const storeOptions: SearchableDropdownOption[] = (storesQuery.data?.items ?? []).map(store => ({
    value: String(store.id),
    label: store.name
  }));

  return {
    brandOptions,
    brandsLoading: brandsQuery.isLoading,
    developerOptions,
    developersLoading: developersQuery.isLoading,
    storeOptions,
    storesLoading: storesQuery.isFetching,
    searchStores: setStoreSearch
  };
};
